import * as i0 from '@angular/core';
import { InjectionToken, ComponentFactoryResolver, NgModuleFactory, Injectable, Inject, NgZone, Injector, ViewContainerRef, Component, Input, ViewChild, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { from, merge } from 'rxjs';
import { map } from 'rxjs/operators';

/* Injection token to provide the element path modules. */
const LAZY_CMPS_REGISTRY = new InjectionToken('ngx-lazy-cmp-registry');
function createDef(selector, loadChildren) {
    return { selector, loadChildren };
}

class NgxElementService {
    constructor(registry, compiler, injector) {
        this.registry = registry;
        this.compiler = compiler;
        this.injector = injector;
        this.loadedComponents = new Map();
        this.elementsLoading = new Map();
        this.injectors = new Map();
        this.componentFactoryResolvers = new Map();
        const ELEMENT_MODULE_PATHS = new Map();
        registry.definitions.forEach(route => {
            ELEMENT_MODULE_PATHS.set(route.selector, route);
        });
        this.componentsToLoad = ELEMENT_MODULE_PATHS;
    }
    receiveContext(component, injector) {
        this.injectors.set(component, injector);
        this.componentFactoryResolvers.set(component, injector.get(ComponentFactoryResolver));
    }
    getInjector(component) {
        return this.injectors.get(component);
    }
    getComponentFactoryResolver(component) {
        return this.componentFactoryResolvers.get(component);
    }
    getComponentsToLoad() {
        return this.componentsToLoad;
    }
    getComponentToLoad(selector) {
        // Returns observable that completes when the lazy module has been loaded.
        const registered = this.loadComponent(selector);
        return from(registered);
    }
    /**
     * Checks whether the selector is registered in the registry.
     * @param selector
     */
    isSelectorRegistered(selector) {
        let result = false;
        this.registry.definitions.forEach(def => {
            if (selector === def.selector) {
                result = true;
            }
        });
        return result;
    }
    /**
     * Allows to lazy load a component given its selector.
     * If the component selector has been registered, it's according module
     * will be fetched lazily
     * @param componentTag selector of the component to load
     */
    loadComponent(componentSelector) {
        if (this.elementsLoading.has(componentSelector)) {
            return this.elementsLoading.get(componentSelector);
        }
        if (this.componentsToLoad.has(componentSelector)) {
            const cmpRegistryEntry = this.componentsToLoad.get(componentSelector);
            const path = cmpRegistryEntry.loadChildren;
            const loadPromise = new Promise((resolve, reject) => {
                path()
                    .then(elementModuleOrFactory => {
                    /**
                     * With View Engine, the NgModule factory is created and provided when loaded.
                     * With Ivy, only the NgModule class is provided loaded and must be compiled.
                     * This uses the same mechanism as the deprecated `SystemJsNgModuleLoader` in
                     * in `packages/core/src/linker/system_js_ng_module_factory_loader.ts`
                     * to pass on the NgModuleFactory, or compile the NgModule and return its NgModuleFactory.
                     */
                    if (elementModuleOrFactory instanceof NgModuleFactory) {
                        return elementModuleOrFactory;
                    }
                    else {
                        try {
                            return this.compiler.compileModuleAsync(elementModuleOrFactory);
                        }
                        catch (err) {
                            // return the error
                            reject(err);
                            // break the promise chain
                            throw err;
                        }
                    }
                })
                    .then(moduleFactory => {
                    const elementModuleRef = moduleFactory.create(this.injector);
                    let componentClass;
                    if (typeof elementModuleRef.instance.customElementComponent === 'object') {
                        componentClass = elementModuleRef.instance.customElementComponent[componentSelector];
                        if (!componentClass) {
                            // tslint:disable-next-line: no-string-throw
                            throw `You specified multiple component elements in module ${elementModuleRef} but there was no match for tag
                        ${componentSelector} in ${JSON.stringify(elementModuleRef.instance.customElementComponent)}.
                         Make sure the selector in the module is aligned with the one specified in the lazy module definition.`;
                        }
                    }
                    else {
                        componentClass = elementModuleRef.instance.customElementComponent;
                    }
                    // Register injector of the lazy module.
                    // This is needed to share the entryComponents between the lazy module and the application
                    const moduleInjector = elementModuleRef.injector;
                    this.receiveContext(componentClass, moduleInjector);
                    this.loadedComponents.set(componentSelector, componentClass);
                    this.elementsLoading.delete(componentSelector);
                    this.componentsToLoad.delete(componentSelector);
                    resolve({
                        selector: componentSelector,
                        componentClass
                    });
                })
                    .catch(err => {
                    this.elementsLoading.delete(componentSelector);
                    return Promise.reject(err);
                });
            });
            this.elementsLoading.set(componentSelector, loadPromise);
            return loadPromise;
        }
        else if (this.loadedComponents.has(componentSelector)) {
            // component already loaded
            return new Promise(resolve => {
                resolve({
                    selector: componentSelector,
                    componentClass: this.loadedComponents.get(componentSelector)
                });
            });
        }
        else {
            throw new Error(`Unrecognized component "${componentSelector}". Make sure it is registered in the component registry`);
        }
    }
}
NgxElementService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementService, deps: [{ token: LAZY_CMPS_REGISTRY }, { token: i0.Compiler }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
NgxElementService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LAZY_CMPS_REGISTRY]
                }] }, { type: i0.Compiler }, { type: i0.Injector }]; } });

class NgxElementComponent {
    constructor(ngxElementService, elementRef, ngZone, registry) {
        this.ngxElementService = ngxElementService;
        this.elementRef = elementRef;
        this.ngZone = ngZone;
        this.registry = registry;
    }
    /**
     * Add input proxies of a lazy loaded and dynamically instantiated Angular component
     * and set the on the actual element when it is ready.
     */
    setProxiedInputs(factory) {
        // Apply proxy to every input defined by factory
        factory.inputs.forEach(input => {
            const comRef = this.componentRef;
            const propName = input.propName;
            const setter = this.buildZonedSetter(comRef, propName);
            if (this.elementRef.nativeElement[propName] !== undefined) {
                comRef.instance[propName] = this.elementRef.nativeElement[propName];
            }
            Object.defineProperty(this.elementRef.nativeElement, propName, {
                get() {
                    return comRef.instance[propName];
                },
                set(value) {
                    setter(value);
                }
            });
        });
    }
    /**
     * Subscribe to event emitters of a lazy loaded and dynamically instantiated Angular component
     * and dispatch them as Custom Events on the NgxElementComponent that is used in a template.
     */
    setProxiedOutputs(factory) {
        const eventEmitters = factory.outputs.map(({ propName, templateName }) => {
            const emitter = this.componentRef.instance[propName];
            return emitter.pipe(map((value) => ({ name: templateName, value })));
        });
        const outputEvents = merge(...eventEmitters);
        this.ngElementEventsSubscription = outputEvents.subscribe(subscription => {
            const customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent(subscription.name, false, false, subscription.value);
            this.elementRef.nativeElement.dispatchEvent(customEvent);
        });
    }
    buildZonedSetter(componentRef, propertyName) {
        const setter = (value) => componentRef.instance[propertyName] = value;
        return (value) => {
            NgZone.isInAngularZone()
                ? setter(value)
                : this.ngZone.run(() => setter(value));
            componentRef.instance[propertyName] = value;
        };
    }
    ngOnInit() {
        const selector = this.resolveSelector();
        this.ngxElementService.getComponentToLoad(selector).subscribe(event => {
            this.componentToLoad = event.componentClass;
            this.componentFactoryResolver = this.ngxElementService.getComponentFactoryResolver(this.componentToLoad);
            this.injector = this.ngxElementService.getInjector(this.componentToLoad);
            const attributes = this.getElementAttributes();
            this.createComponent(attributes);
        });
    }
    createComponent(attributes) {
        const factory = this.componentFactoryResolver.resolveComponentFactory(this.componentToLoad);
        if (this.registry.useCustomElementNames && this.ngxElementService.isSelectorRegistered(factory.selector)) {
            console.warn(`Cannot lazy load component that defines ${factory.selector} as a selector, because the selector is
                    already reserved in the LazyComponentRegistry.`);
            return;
        }
        this.refInjector = Injector.create({ providers: [{ provide: this.componentToLoad, useValue: this.componentToLoad }] });
        const projectNodes = this.extractProjectedNodes(factory);
        this.container.clear();
        this.componentRef = this.container.createComponent(factory, 0, this.refInjector, projectNodes);
        this.setAttributes(attributes);
        this.listenToAttributeChanges();
        this.setProxiedInputs(factory);
        this.setProxiedOutputs(factory);
    }
    setAttributes(attributes) {
        attributes.forEach(attr => {
            this.componentRef.instance[attr.name] = attr.value;
        });
    }
    getElementAttributes() {
        const attrs = this.elementRef.nativeElement.attributes;
        const attributes = [];
        for (let attr, i = 0; i < attrs.length; i++) {
            attr = attrs[i];
            if ((!this.registry.useCustomElementNames && attr.nodeName.match('^data-')) || this.registry.useCustomElementNames) {
                attributes.push({
                    name: this.camelCaseAttribute(attr.nodeName),
                    value: attr.nodeValue
                });
            }
        }
        return attributes;
    }
    camelCaseAttribute(attribute) {
        const attr = attribute.replace('data-', '');
        const chunks = attr.split('-');
        if (chunks.length > 1) {
            return chunks[0] + chunks.slice(1).map(chunk => chunk.replace(/^\w/, c => c.toUpperCase())).join('');
        }
        return attr;
    }
    listenToAttributeChanges() {
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'attributes') {
                    const attributes = this.getElementAttributes();
                    this.setAttributes(attributes);
                }
            });
        });
        observer.observe(this.elementRef.nativeElement, {
            attributes: true
        });
    }
    ngOnDestroy() {
        if (this.componentRef != null) {
            this.componentRef.destroy();
        }
        if (this.ngElementEventsSubscription != null) {
            this.ngElementEventsSubscription.unsubscribe();
        }
    }
    extractProjectedNodes(factory) {
        const projectNodes = [];
        factory.ngContentSelectors.forEach(selector => {
            const el = this.elementRef.nativeElement;
            const content = el.querySelectorAll(selector);
            if (content) {
                const nodes = [];
                content.forEach(c => {
                    const p = c.parentElement;
                    nodes.push(p.removeChild(c));
                });
                projectNodes.push(nodes);
            }
        });
        return projectNodes;
    }
    resolveSelector() {
        return this.registry.useCustomElementNames ?
            this.elementRef.nativeElement.localName.substring(this.registry.customElementNamePrefix.length + 1) :
            this.selector;
    }
}
NgxElementComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementComponent, deps: [{ token: NgxElementService }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: LAZY_CMPS_REGISTRY }], target: i0.ɵɵFactoryTarget.Component });
NgxElementComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.0", type: NgxElementComponent, selector: "ng-component", inputs: { selector: "selector" }, viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true, read: ViewContainerRef }], ngImport: i0, template: `
    <ng-template #container></ng-template>
    <ng-content></ng-content>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementComponent, decorators: [{
            type: Component,
            args: [{
                    template: `
    <ng-template #container></ng-template>
    <ng-content></ng-content>
  `,
                    styles: []
                }]
        }], ctorParameters: function () { return [{ type: NgxElementService }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LAZY_CMPS_REGISTRY]
                }] }]; }, propDecorators: { selector: [{
                type: Input
            }], container: [{
                type: ViewChild,
                args: ['container', { read: ViewContainerRef }]
            }] } });

class NgxElementModule {
    constructor(injector, registry) {
        this.injector = injector;
        this.registry = registry;
        if (!registry.useCustomElementNames) {
            const ngxElement = createCustomElement(NgxElementComponent, { injector });
            customElements.define('ngx-element', ngxElement);
        }
        else {
            registry.definitions.forEach(def => {
                const ngxElement = createCustomElement(NgxElementComponent, { injector });
                customElements.define(`${registry.customElementNamePrefix}-${def.selector}`, ngxElement);
            });
        }
    }
    static forRoot(registry) {
        return {
            ngModule: NgxElementModule,
            providers: [
                {
                    provide: LAZY_CMPS_REGISTRY,
                    useValue: registry
                }
            ]
        };
    }
    ngDoBootstrap() { }
}
NgxElementModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementModule, deps: [{ token: i0.Injector }, { token: LAZY_CMPS_REGISTRY }], target: i0.ɵɵFactoryTarget.NgModule });
NgxElementModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementModule, declarations: [NgxElementComponent] });
NgxElementModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: NgxElementModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [NgxElementComponent],
                    entryComponents: [NgxElementComponent]
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LAZY_CMPS_REGISTRY]
                }] }]; } });

/*
 * Public API Surface of ngx-element
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LAZY_CMPS_REGISTRY, NgxElementModule, createDef };
